import React from "react";
import { useMoney } from "../../../hooks/useMoney";
import { DollarSign, Wallet, Plus, CheckCircle, TrendingUp } from "lucide-react";
import type { FYPreviewResponse } from "../../../features/distributor/DistributorSlice";
import { formatDateForDisplay } from "./yearEndUtils";

interface YearEndPreviewResultProps {
  data: FYPreviewResponse;
}

const WalletStyleBlock: React.FC<{
  title: string;
  subtitle?: string;
  balanceLabel: string;
  balanceValue: string;
  faceValueLabel: string;
  faceValueValue: string;
  topUpLabel: string;
  topUpValue: string;
  rightTitle: string;
  rightContent: React.ReactNode;
}> = ({
  title,
  subtitle,
  balanceLabel,
  balanceValue,
  faceValueLabel,
  faceValueValue,
  topUpLabel,
  topUpValue,
  rightTitle,
  rightContent,
}) => (
  <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-baseline justify-between gap-3 mb-4">
      <div>
        <h5 className="text-sm font-semibold text-gray-800">{title}</h5>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="flex flex-row gap-6 flex-wrap">
      <div className="flex-1 min-w-[260px]">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Wallet Details</h4>
        <div className="flex flex-col gap-2">
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <div className="p-2 mr-5 rounded-full bg-white">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">{balanceLabel}</p>
              <p className="text-md mt-1 font-semibold text-gray-900">{balanceValue}</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 rounded-lg w-full">
            <div className="p-2 mr-5 rounded-full bg-white">
              <Wallet className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">{faceValueLabel}</p>
              <p className="text-md mt-1 font-semibold text-gray-900">{faceValueValue}</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <div className="p-2 mr-5 rounded-full bg-white">
              <Plus className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">{topUpLabel}</p>
              <p className="text-md mt-1 font-semibold text-gray-900">{topUpValue}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-[260px]">
        <h4 className="text-sm font-medium text-gray-700 mb-3">{rightTitle}</h4>
        {rightContent}
      </div>
    </div>
  </article>
);

const YearEndPreviewResult: React.FC<YearEndPreviewResultProps> = ({ data }) => {
  const { symbol } = useMoney();
  const closing = data.closing_FY_balance;
  const carried = data.carried_FY_balance;
  const transactions = data.new_FY_transactions;
  const wallet = data.current_wallet_balance;

  return (
    <section className="space-y-6 pt-2">
      <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
        <div className="p-1.5 rounded-lg bg-brand/10">
          <TrendingUp className="w-4 h-4 text-brand" />
        </div>
        <h4 className="text-base font-semibold text-gray-800">
          Preview: Changes that will occur
        </h4>
      </div>

      <WalletStyleBlock
        title="Closing FY balance"
        subtitle={`FY: ${formatDateForDisplay(closing.FY_start)} – ${formatDateForDisplay(closing.FY_end)}`}
        balanceLabel="Closing Balance"
        balanceValue={`${symbol}${closing.closing_balance?.toLocaleString() ?? "—"}`}
        faceValueLabel="Face Value"
        faceValueValue={`${symbol}${closing.total_face_value?.toLocaleString() ?? "—"}`}
        topUpLabel="Total Top Up"
        topUpValue={`${symbol}${closing.total_top_up?.toLocaleString() ?? "—"}`}
        rightTitle="Discount Ranges"
        rightContent={
          <>
            {closing.remark && (
              <p className="text-xs text-gray-600 mb-2">Remark: {closing.remark}</p>
            )}
            {closing.discount_range?.length ? (
              <div className="space-y-2">
                {closing.discount_range.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm text-gray-700">
                        {symbol}{r.min_order_value} to {r.max_order_value == null ? "∞" : `${symbol}${r.max_order_value}`}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-brand">{r.discount}% discount</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">No discount ranges available</div>
            )}
          </>
        }
      />

      {/* 2. Carried FY + New FY transactions (2-col) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h5 className="text-sm font-semibold text-gray-800 mb-4">Carried FY balance</h5>
          <dl className="space-y-3">
            {[
              { label: "Current balance", value: `${symbol}${carried.current_balance?.toLocaleString() ?? "—"}` },
              { label: "Cumulative face value", value: `${symbol}${carried.cumulative_face_value?.toLocaleString() ?? "—"}` },
              { label: "Cumulative top up", value: `${symbol}${carried.cumulative_top_up?.toLocaleString() ?? "—"}` },
              { label: "Discount %", value: `${carried.discountPercentage ?? "—"}%` },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between items-baseline gap-2 text-sm">
                <dt className="text-gray-600 shrink-0">{label}</dt>
                <dd className="font-medium text-gray-900 text-right truncate">{value}</dd>
              </div>
            ))}
          </dl>
        </article>
        <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h5 className="text-sm font-semibold text-gray-800 mb-4">New FY transactions</h5>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 rounded-lg bg-green-50 border border-green-100">
              <span className="text-sm text-gray-700">Credit</span>
              <span className="font-semibold text-green-700">
                {symbol}{transactions.creditTransactionAmt?.toLocaleString() ?? "—"}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-red-50 border border-red-100">
              <span className="text-sm text-gray-700">Debit</span>
              <span className="font-semibold text-red-700">
                {symbol}{transactions.debitTransactionAmt?.toLocaleString() ?? "—"}
              </span>
            </div>
          </div>
        </article>
      </div>

      {/* 3. Current wallet balance (after close) – same wallet structure as above */}
      <WalletStyleBlock
        title="Current wallet balance (after close)"
        subtitle={`New contract: ${formatDateForDisplay(wallet.new_contract_start_date)} – ${formatDateForDisplay(wallet.new_contract_end_date)}`}
        balanceLabel="Current Balance"
        balanceValue={`${symbol}${wallet.current_balance?.toLocaleString() ?? "—"}`}
        faceValueLabel="Face Value"
        faceValueValue={`${symbol}${wallet.total_face_value?.toLocaleString() ?? "—"}`}
        topUpLabel="Total Top Up"
        topUpValue={`${symbol}${wallet.total_top_up?.toLocaleString() ?? "—"}`}
        rightTitle="Discount Ranges"
        rightContent={
          <>
            {wallet.new_discount_ranges?.length ? (
              <div className="space-y-2">
                {wallet.new_discount_ranges.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm text-gray-700">
                        {symbol}{r.min_order_value} to {r.max_order_value == null ? "∞" : `${symbol}${r.max_order_value}`}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-brand">{r.discount}% discount</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">No discount ranges available</div>
            )}
          </>
        }
      />
    </section>
  );
};

export default YearEndPreviewResult;
